import * as THREE from 'three';
import {
  CAM_DISTANCE,
  CAM_HEIGHT,
  CAM_MIN_POLAR,
  CAM_MAX_POLAR,
  CAM_FOLLOW_SPEED,
} from '../config';
import { clamp, lerpAngle } from '../utils/math';
import { CollisionWorld } from '../collision/CollisionWorld';

// Camera-occlusion tuning. CAM_MIN_DISTANCE keeps the camera off the player's
// face when crammed into a corner. CAM_OCCLUSION_SKIN is the gap kept between
// camera and wall so the near-plane never clips into geometry.
const CAM_MIN_DISTANCE = 1.5;
const CAM_OCCLUSION_SKIN = 0.4;
// Recovery rate when the path clears: low value = slow ease-out so the camera
// doesn't yo-yo when you pass an alleyway. Pull-in is instant by design.
const CAM_RECOVERY_SPEED = 4;

export class ThirdPersonCamera {
  private camera: THREE.PerspectiveCamera;
  private collisionWorld: CollisionWorld;
  private target = new THREE.Vector3();
  private smoothTarget = new THREE.Vector3();
  private initialized = false;

  // Spherical coordinates
  private yaw = Math.PI; // Start looking from behind (south)
  private pitch = 25 * (Math.PI / 180); // 25 degrees — nice low angle
  private distance = CAM_DISTANCE; // smoothed effective distance after occlusion

  constructor(camera: THREE.PerspectiveCamera, collisionWorld: CollisionWorld) {
    this.camera = camera;
    this.collisionWorld = collisionWorld;
  }

  rotate(deltaX: number, deltaY: number) {
    this.yaw -= deltaX;
    this.pitch = clamp(this.pitch + deltaY, CAM_MIN_POLAR, CAM_MAX_POLAR);
  }

  getYaw(): number {
    return this.yaw;
  }

  update(delta: number, targetPos: THREE.Vector3, characterRotationY?: number) {
    // Look at chest height of character
    this.target.copy(targetPos);
    this.target.y += CAM_HEIGHT * 0.5;

    const firstFrame = !this.initialized;

    // Snap on first frame, lerp after
    if (firstFrame) {
      this.smoothTarget.copy(this.target);
      this.initialized = true;
    } else {
      const factor = 1 - Math.exp(-CAM_FOLLOW_SPEED * delta);
      this.smoothTarget.lerp(this.target, factor);
    }

    // Auto-rotate camera behind the character when they're moving
    if (characterRotationY != null) {
      // Camera should be behind the character: character faces angle → camera at angle + PI
      const desiredYaw = characterRotationY + Math.PI;
      const rotSpeed = 3; // how fast camera swings behind
      const factor = 1 - Math.exp(-rotSpeed * delta);
      this.yaw = lerpAngle(this.yaw, desiredYaw, factor);
    }

    // Direction from player toward where the camera *wants* to sit.
    const dirX = Math.sin(this.yaw) * Math.cos(this.pitch);
    const dirY = Math.sin(this.pitch);
    const dirZ = Math.cos(this.yaw) * Math.cos(this.pitch);

    // Raycast against building/wall AABBs. If something is in the way, the
    // target distance shrinks to land just before the obstruction.
    const hitDist = this.collisionWorld.raycast(
      this.smoothTarget.x, this.smoothTarget.y, this.smoothTarget.z,
      dirX, dirY, dirZ,
      CAM_DISTANCE,
    );
    const targetDistance = clamp(hitDist - CAM_OCCLUSION_SKIN, CAM_MIN_DISTANCE, CAM_DISTANCE);

    // Asymmetric smoothing: snap in immediately to never reveal a wall's
    // backface, ease out gradually so the camera doesn't bounce when the
    // occluder clears.
    if (targetDistance < this.distance) {
      this.distance = targetDistance;
    } else {
      const factor = 1 - Math.exp(-CAM_RECOVERY_SPEED * delta);
      this.distance += (targetDistance - this.distance) * factor;
    }

    const offset = new THREE.Vector3(
      dirX * this.distance,
      dirY * this.distance,
      dirZ * this.distance,
    );

    const cameraPos = this.smoothTarget.clone().add(offset);

    // Ensure camera doesn't go below ground
    if (cameraPos.y < 1.5) cameraPos.y = 1.5;

    if (firstFrame) {
      this.camera.position.copy(cameraPos);
    } else {
      const factor = 1 - Math.exp(-CAM_FOLLOW_SPEED * delta);
      this.camera.position.lerp(cameraPos, factor);
    }
    this.camera.lookAt(this.smoothTarget);
  }
}
